import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { containsProfanity } from '../../utils/profanityUtils';
import AvatarSelector from '../Auth/SignUp/AvatarSelector';
import ErrorMessage from '../common/ErrorMessage';
import LoadingButton from '../common/LoadingButton';

const EditProfileModal = ({ isOpen, onClose, currentUser, onUpdate }) => {
    const { updateProfile } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        avatar_color: '',
        jersey_number: '',
        password: ''
    });

    // Update form data when currentUser changes
    useEffect(() => {
        if (currentUser) {
            setFormData({
                username: currentUser.username,
                avatar_color: currentUser.avatar_color,
                jersey_number: currentUser.jersey_number
            });
        }
    }, [currentUser]);
    const [error, setError] = useState('');

    const handleCustomizationUpdate = ({ color, jerseyNumber }) => {
        setFormData(prev => ({
            ...prev,
            avatar_color: color || prev.avatar_color,
            jersey_number: jerseyNumber || prev.jersey_number
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            const username = formData.username.trim();

            // Add this log
            console.log('Checking profanity for:', username);
            if (containsProfanity(username)) {
                console.log('Profanity detected!');  // Add this log
                setError('Username contains inappropriate content');
                return;
            }

            // Only include password in update data if it's not empty
            const updateData = {
                username: formData.username,
                avatar_color: formData.avatar_color,
                jersey_number: formData.jersey_number,
            };

            if (formData.password) {
                updateData.password = formData.password;
            }

            await updateProfile(updateData);
            onUpdate();
            onClose();
        } catch (error) {
            // First log the raw error object
            console.error('Raw error object:', error);

            // Log the specific response and its data
            if (error.response) {
                console.log('Error response status:', error.response.status);
                console.log('Error response data:', error.response.data);
            }

            const errorData = error.response?.data;

            if (errorData?.username?.[0]) {
                setError(errorData.username[0]);
            } else if (errorData?.detail) {
                setError(errorData.detail);
            } else {
                setError('An error occurred during submission');
            }
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-lg p-6 max-w-md w-full"
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-2xl">Edit Profile</h2>
                        <button
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                maxLength={11}
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                value={formData.username}
                                autoComplete="username"
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    username: e.target.value
                                }))}
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                New Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                value={formData.password}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    password: e.target.value
                                }))}
                                placeholder="Choose a password"
                                minLength={8}
                            />
                        </div>

                        <div>
                            <AvatarSelector
                                onSelectAvatar={handleCustomizationUpdate}
                                initialColor={currentUser?.avatar_color}
                                initialNumber={currentUser?.jersey_number}
                                username={formData.username}
                            />
                        </div>

                        <ErrorMessage error={error} />

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                            <LoadingButton
                                type="submit"
                                className="btn-primary"
                                loadingKey="editProfile"
                            >
                                Save
                            </LoadingButton>
                        </div>
                    </form>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default EditProfileModal;
